import { ITEM_RARITY_NAMES, resolveUpgradeStats } from '@sorrem/shared';
import { InlayPanel } from '@sorrem-client/ui/components';
import type { CraftingWorkbenchState } from '@sorrem-client/ui/features/CharacterHome/hooks';
import { toEquipment } from '@sorrem-client/ui/shared/inventory-item';
import { findUpgradeTarget, resolveUpgradePath } from '../../crafting-preview';
import { useCraftingItem } from '../../use-crafting-item';
import { RerollAffix } from '../RerollAffix';
import { UpgradePath } from '../UpgradePath';
import { WorkbenchItem } from '../WorkbenchItem';
import { WorkbenchUpgrade } from '../WorkbenchUpgrade';
import styles from './CraftingWorkbench.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface CraftingWorkbenchProps {
    readonly workbench: CraftingWorkbenchState;
}

/**
 * Crafting's workbench (#287): what the selected item is and what crafting
 * would make it, all in one view. The item's card, large, with a reroll
 * link on each affix, and beside it a column of level cards: Current, then
 * each upgrade level ahead. The item's card shows the item at the chosen
 * level, its stats flashing with each change of choice, and under it the
 * upgrade to that level. The vessel panel beside the workbench shows what
 * the upgrade, or the reroll being looked at, leaves.
 */
export function CraftingWorkbench({ workbench }: CraftingWorkbenchProps): JSX.Element {
    const { character, item, preview } = useCraftingItem();
    const { previewReroll, resolveTargetLevel, chooseTargetLevel, crafting } = workbench;
    const { isWorking, error, reroll, upgradeBy } = crafting;

    const body = ((): JSX.Element => {
        if (item === null || preview === null) {
            return <p className={styles.note}>Select an item, equipped, carried or stored, to work on it.</p>;
        }
        if (preview.isNamed && item.instance) {
            return <p className={styles.note}>{`${ITEM_RARITY_NAMES[item.instance.rarity]} items are already whole: they can't be upgraded or rerolled.`}</p>;
        }
        const path = resolveUpgradePath(item);
        const targetLevel = resolveTargetLevel(item.id, preview.upgradeLevel);
        const rerollCost = preview.rerollCost;
        // The item as it would be at the chosen level; as it is, on the Current card.
        const shown = findUpgradeTarget(path, targetLevel)?.item ?? item;
        // A failure shows only where it happened: this item, and the action that failed.
        const errorFor = (action: 'upgrade' | 'reroll'): string | null => (error !== null && error.itemId === item.id && error.action === action ? error.message : null);
        return (
            <section className={styles.upgrade} aria-label='Workbench'>
                <div className={styles.itemColumn}>
                    <WorkbenchItem
                        key={item.id}
                        item={shown}
                        affixAction={character === null || rerollCost === undefined ? undefined : (index): JSX.Element => (
                            <RerollAffix
                                item={item}
                                affixIndex={index}
                                banked={character.permanentEssence}
                                cost={rerollCost}
                                isWorking={isWorking}
                                error={errorFor('reroll')}
                                onReroll={() => reroll(item.id, index)}
                                onPreview={(isPreviewed) => previewReroll(item.id, isPreviewed ? index : null)}
                            />
                        )}
                    />
                    {character !== null && (
                        <div className={styles.upgradeArea}>
                            <WorkbenchUpgrade
                                item={item}
                                currentLevel={preview.upgradeLevel}
                                targetLevel={targetLevel}
                                banked={character.permanentEssence}
                                isWorking={isWorking}
                                error={errorFor('upgrade')}
                                onUpgrade={(levels) => void upgradeBy(item.id, levels)}
                            />
                        </div>
                    )}
                </div>
                {path.length > 0 && (
                    <UpgradePath
                        rows={path}
                        currentLevel={preview.upgradeLevel}
                        currentStats={resolveUpgradeStats(toEquipment(item))}
                        targetLevel={targetLevel}
                        onChooseLevel={(level) => chooseTargetLevel(item.id, level)}
                    />
                )}
            </section>
        );
    })();

    return (
        <InlayPanel className={styles.craftingWorkbench} {...toTourTargetProps('crafting-workbench')}>
            <div className={styles.body}>{body}</div>
        </InlayPanel>
    );
}
